import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IonButton, IonIcon, IonSpinner } from '@ionic/angular/standalone';

@Component({
  selector: 'crowdctrl-loading-button',
  standalone: true,
  imports: [IonButton, IonSpinner, IonIcon, RouterLink],
  templateUrl: './loading-button.component.html',
  styleUrl: './loading-button.component.css',
})
export class LoadingButtonComponent {
  @Input() loading = false;
  @Input() disabled = false;
  @Input() loadingText = '';
  @Input() startIcon: string | null = null;
  @Input() endIcon: string | null = null;

  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() expand?: 'block' | 'full';
  @Input() fill?: 'clear' | 'outline' | 'solid' | 'default';
  @Input() color?: string;
  @Input() size?: 'small' | 'default' | 'large';
  @Input() shape?: 'round';
  @Input() strong?: boolean;

  @Input() routerLink?: string | any[];
  @Input() routerDirection?: 'forward' | 'back' | 'root';
  @Input() href?: string;
  @Input() target?: string;
  @Input() download?: string;
  @Input() rel?: string;

  /**
   * Fired from the inner `ion-button` when not {@link loading} or {@link disabled}.
   * Listens to both `ionClick` and native `click`: some stacks (nested lists, certain WebViews)
   * drop `ionClick` while `click` still fires; both can fire together, so we coalesce.
   * Named `pressed` (not `click`) so parent `(pressed)` is not confused with host DOM `click`.
   */
  @Output() pressed = new EventEmitter<Event>();

  /** Last time we emitted `pressed` from inner ion-button (ms); avoids double-fire from ionClick+click. */
  private lastInnerPressEmittedMs = 0;

  @HostBinding('class.expand-block')
  get hasExpandBlock(): boolean {
    return this.expand === 'block';
  }

  get isDisabled(): boolean {
    return this.loading || this.disabled;
  }

  onInnerPress(event: Event): void {
    if (this.isDisabled) {
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastInnerPressEmittedMs < 400) {
      return;
    }
    this.lastInnerPressEmittedMs = now;
    this.pressed.emit(event);
  }
}
